// Single entry point for all error/event reporting. Today this routes to
// OpenObserve (RUM + Logs + Session Replay); if we ever swap providers again
// (Sentry, Datadog, LogRocket, …) only this file changes. Call sites should
// never import the underlying SDK directly.

import { openobserveRum, DefaultPrivacyLevel } from '@openobserve/browser-rum';
import { openobserveLogs } from '@openobserve/browser-logs';

export type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

export type LogAttributes = Record<string, string | number | boolean | undefined>;

export interface LogContext {
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  level?: LogLevel;
}

export interface BreadcrumbInput {
  category: string;
  message: string;
  level?: LogLevel;
  data?: Record<string, unknown>;
}

export interface UserIdentity {
  id?: string;
  email?: string;
  username?: string;
  [key: string]: unknown;
}

// Config shape consumed by initTelemetry(). Mirrors the `openobserve` block in
// each environment file so main.ts can forward it verbatim.
export interface TelemetryConfig {
  enabled: boolean;
  clientToken: string;
  applicationId: string;
  site: string;
  organizationIdentifier: string;
  service: string;
  env: string;
  version: string;
  apiVersion: string;
  insecureHTTP?: boolean;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  defaultPrivacyLevel?: 'allow' | 'mask' | 'mask-user-input';
  trackResources?: boolean;
  trackLongTasks?: boolean;
  trackUserInteractions?: boolean;
  enableSessionReplay?: boolean;
  // Backend URLs that should receive distributed-tracing headers (the
  // OpenObserve analog of Sentry's tracePropagationTargets).
  allowedTracingUrls?: string[];
}

let initialized = false;

// Noisy browser errors that don't represent real bugs — dropped in beforeSend.
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /Non-Error promise rejection captured/i,
  // Google Translate iframe (login page) emits cross-origin script errors.
  /translate\.google\.com/i
];

function isIgnoredMessage(message: string): boolean {
  return IGNORED_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * Initialise OpenObserve RUM + Logs. Safe to call once; subsequent calls and
 * the disabled/missing-token case are no-ops. Invoked from main.ts before the
 * Angular app bootstraps.
 */
export function initTelemetry(config: TelemetryConfig): void {
  if (initialized || !config?.enabled || !config.clientToken) {
    return;
  }

  openobserveRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: config.site,
    organizationIdentifier: config.organizationIdentifier,
    service: config.service,
    env: config.env,
    version: config.version,
    apiVersion: config.apiVersion,
    insecureHTTP: config.insecureHTTP ?? false,
    sessionSampleRate: config.sessionSampleRate,
    sessionReplaySampleRate: config.sessionReplaySampleRate,
    // Form inputs masked by default; tag extra-sensitive text nodes with the
    // `oo-privacy-mask` CSS class (or data-oo-privacy="mask") to mask in replay.
    defaultPrivacyLevel: (config.defaultPrivacyLevel ?? 'mask-user-input') as DefaultPrivacyLevel,
    trackResources: config.trackResources ?? true,
    trackLongTasks: config.trackLongTasks ?? true,
    trackUserInteractions: config.trackUserInteractions ?? true,
    allowedTracingUrls: config.allowedTracingUrls,
    beforeSend: (event) => {
      if (event.type === 'error' && isIgnoredMessage(event.error?.message ?? '')) {
        return false;
      }
      return true;
    }
  });

  openobserveLogs.init({
    clientToken: config.clientToken,
    site: config.site,
    organizationIdentifier: config.organizationIdentifier,
    service: config.service,
    env: config.env,
    version: config.version,
    apiVersion: config.apiVersion,
    insecureHTTP: config.insecureHTTP ?? false,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    beforeSend: (event) => !isIgnoredMessage(String(event.message ?? ''))
  });

  // Record replays unless explicitly disabled. RUM only uploads up to
  // sessionReplaySampleRate% of sessions regardless.
  if ((config.enableSessionReplay ?? true) && config.sessionReplaySampleRate > 0) {
    openobserveRum.startSessionReplayRecording();
  }

  initialized = true;
}

// Map our LogLevel onto an openobserveLogs.logger method name.
function loggerMethod(level: LogLevel): 'debug' | 'info' | 'warn' | 'error' {
  switch (level) {
    case 'debug':
      return 'debug';
    case 'info':
      return 'info';
    case 'warning':
      return 'warn';
    case 'error':
    case 'fatal':
      return 'error';
    default:
      return 'info';
  }
}

// OpenObserve attaches a single flat context object to errors/logs/actions.
// Flatten Sentry-style { tags, extra, contexts } into one bag so existing call
// sites keep working unchanged.
function flattenContext(ctx?: LogContext, base?: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(base ?? {}) };
  if (!ctx) return out;
  if (ctx.level) out['level'] = ctx.level;
  if (ctx.tags) Object.assign(out, ctx.tags);
  if (ctx.extra) Object.assign(out, ctx.extra);
  if (ctx.contexts) Object.assign(out, ctx.contexts);
  return out;
}

/**
 * Contextual trail leading up to an error. OpenObserve has no native
 * breadcrumbs, so we record these as RUM custom actions — they appear on the
 * session timeline and inside Session Replay, which is the closest analog.
 */
export function breadcrumb(input: BreadcrumbInput): void {
  openobserveRum.addAction(input.message, {
    category: input.category,
    level: input.level ?? 'info',
    ...(input.data ?? {})
  });
}

/**
 * Send a structured log to OpenObserve's Logs stream.
 * Uses `openobserveLogs.logger.info/warn/error(...)` under the hood.
 */
export function log(level: LogLevel, message: string, attributes?: LogAttributes): void {
  openobserveLogs.logger[loggerMethod(level)](message, attributes);
}

/**
 * Dual-emit a business event as BOTH:
 *   1. A RUM action (context for future crash reports + replay timeline)
 *   2. A structured log (visible in the Logs stream)
 *
 * Use this for happy-path operations (advance stage, cancel, ship, etc.).
 */
export function event(name: string, attributes?: LogAttributes, level: LogLevel = 'info'): void {
  openobserveRum.addAction(name, {
    category: name.split('.')[0] || 'event',
    level,
    ...(attributes ?? {})
  });
  log(level, name, attributes);
}

export function captureException(error: unknown, ctx?: LogContext): void {
  openobserveRum.addError(error, flattenContext(ctx));
}

export function captureMessage(message: string, ctx?: LogContext): void {
  log(ctx?.level ?? 'info', message, flattenContext(ctx) as LogAttributes);
}

export function setUser(user: UserIdentity | null): void {
  if (!user) {
    openobserveRum.clearUser();
    openobserveLogs.clearUser();
    return;
  }

  const { username, ...rest } = user;
  const identity = { ...rest, name: username } as Record<string, unknown>;
  openobserveRum.setUser(identity as { id: string });
  openobserveLogs.setUser(identity as { id: string });
}

export const telemetry = {
  breadcrumb,
  log,
  event,
  captureException,
  captureMessage,
  setUser
};
